import { useCallback, useEffect, useMemo, useState } from 'react'
import { GoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { Checkbox, Flex, Text, useDisclosure, useToast } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import { ReactComponent as RightArrow } from '../../components/assets/icons/rightArrow.svg'
import MainButton from '../../components/Button/MainButton'
import Card from '../../components/card/Card'
import Header from '../../components/header/Header'
import MainText from '../../components/header/MainText'
import { API_URL } from '../../components/helpers/Helpers'
import SelectInput from '../../components/inputs/SelectInput'
import SimpleInput from '../../components/inputs/SimpleInput'
import TermosDeUsoModal from '../../components/modal/TermosDeUsoModal'
import Wrapper from '../../components/wrapper/Wrapper'
import { licencePlateSchema } from '../../schema/schema'

export type ICities = {
  city: string
  id: number
  status: string
}

export type IArea = {
  id: number
  name: string
}

type IFares = {
  id: number
  minutes: number
  price: number
  vehicle_type: string
  area_id: number
}

const WithoutQRCode = () => {
  const toast = useToast()
  const [cities, setCities] = useState<ICities[]>([])
  const [areaList, setAreaList] = useState<IArea[]>([])
  const [fares, setFares] = useState<IFares[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [captcha, setCaptcha] = useState<string>('')
  const [isChecked, setIsChecked] = useState(false)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [vehicleType, setVehicleType] = useState('')
  const [duration, setDuration] = useState('')
  const [price, setPrice] = useState(0)
  const [fareAreaId, setFareAreaId] = useState<number | null>(null)
  const [isInternationalPlate, setIsInternationalPlate] = useState(false)

  const resolver = useMemo(
    () =>
      yupResolver(
        yup.object().shape({
          celular: yup
            .string()
            .min(11, 'Digite um número válido')
            .transform((value) => value.replace(/[^\d]/g, ''))
            .required(),
          user_name: yup.string().required('Nome e sobrenome são obrigatórios!'),
          city: yup.string().required('Selecione uma cidade'),
          licencePlate: licencePlateSchema(isInternationalPlate),
          vehicle_type: yup.string().trim('Selecione um veículo').required('Selecione um veículo'),
          duration: yup.string().required('Selecione um tempo de duração'),
        }),
      ),
    [isInternationalPlate],
  )

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver,
  })

  const navigate = useNavigate()

  const cityValue = watch('city')
  const areaValue = watch('area')

  useEffect(() => {
    fetch(`${API_URL}/api/operations`)
      .then((res) => res.json())
      .then((data) => {
        setCities(data.data)
      })
  }, [])

  useEffect(() => {
    const id = cities.find((city) => city.city === cityValue)?.id
    if (id) {
      fetch(`${API_URL}/api/operations/id/${id}`)
        .then((res) => res.json())
        .then(({ data }) => {
          setAreaList(data.areas)
          // biome-ignore lint/suspicious/noExplicitAny: idk
          setFares(data.fares.filter((fare: any) => fare.vehicle_type))
        })
    }
  }, [cityValue, cities])

  useEffect(() => {
    setValue('area', '')
    setVehicleType('')
    setDuration('')
    setPrice(0)
    setFareAreaId(null)
  }, [cityValue, setValue])

  useEffect(() => {
    setDuration('')
    setPrice(0)
    setFareAreaId(null)
  }, [areaValue, setValue])

  const areaID = areaList.filter((area) => area.name === watch('area'))[0]?.id
  const cityID = cities.find((city) => city.city === cityValue)?.id

  useEffect(() => {
    const fare = fares.filter((fare) => {
      if (areaID) {
        return fare.vehicle_type === vehicleType && fare.minutes === +duration && fare.area_id === areaID
      } else {
        return fare.vehicle_type === vehicleType && fare.minutes === +duration
      }
    })[0]

    setPrice(fare?.price)
    setFareAreaId(fare?.area_id ?? null)
  }, [duration, vehicleType])

  const filteringVehicles = [...new Set(fares.map((fare) => fare.vehicle_type))]

  const filteringTime = [
    ...new Set(
      fares
        .filter((fare) => {
          if (areaID) {
            return fare.vehicle_type === vehicleType && fare.area_id === areaID
          } else {
            return fare.vehicle_type === vehicleType
          }
        })
        .map((fare) => fare.minutes.toString()),
    ),
  ]

  async function submit(payload: any) {
    const { licencePlate, vehicle_type, duration } = payload
    const newPayload = {
      phone: watch('celular').replace(/[^0-9]/g, ''),
      user_name: watch('user_name'),
      vehicle_type,
      duration,
      plate: licencePlate.toString().toUpperCase(),
      operation_id: cityID,
      price: price,
      area_id: fareAreaId ?? (areaList.length > 0 ? areaList[0].id : null),
      recaptcha: captcha,
    }

    setIsLoading(true)

    fetch(`${API_URL}/api/tickets/payments/pix`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(newPayload),
    })
      .then((res) => res.json())
      .then(({ data, error }) => {
        if (error) {
          toast({
            title: 'Erro ao gerar PIX de pagamento',
            description: error.message,
            status: 'error',
            duration: 3000,
            isClosable: true,
          })
        } else if (data?.id) {
          navigate(`/estacionar/${data.id}`)
        } else {
          toast({
            title: 'Erro ao gerar PIX de pagamento',
            description: 'Erro ao gerar PIX de pagamento',
            status: 'error',
            duration: 3000,
            isClosable: true,
          })
        }
      })
      .catch((error) => console.error(error))
      .finally(() => setIsLoading(false))
  }

  const recaptchaHandler = useCallback((token: string) => {
    setCaptcha(token)
  }, [])

  return (
    <Wrapper opacity={isLoading ? '0.8' : '1'}>
      <Header />
      <MainText text="Olá, preencha os dados abaixo e siga tranquilo." />
      <form
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '30px',
        }}
        onSubmit={handleSubmit(submit)}
      >
        <SimpleInput
          title="PREENCHA O TELEFONE PARA RECEBER OS COMPROVANTES"
          register={register}
          value={watch('celular')}
          name="celular"
          error={errors.celular}
        />
        <SimpleInput
          title="PREENCHA SEU NOME E SOBRENOME"
          register={register}
          name="user_name"
          isVehicle={true}
          value={watch('user_name')}
          error={errors.user_name}
        />
        <SelectInput
          title="EM QUAL CIDADE VOCÊ ESTÁ"
          options={cities}
          register={register}
          name="city"
          error={errors.city}
        />
        <SimpleInput
          title="PLACA DO VEÍCULO"
          register={register}
          name="licencePlate"
          isVehicle={true}
          value={watch('licencePlate')}
          error={errors.licencePlate}
          isInternationalPlate={isInternationalPlate}
          onToggleInternational={setIsInternationalPlate}
        />
        <SelectInput
          title="QUAL TIPO DE VEÍCULO"
          vehicles={filteringVehicles}
          register={register}
          name="vehicle_type"
          error={errors.vehicle_type}
          disabled={!watch('city')}
          value={vehicleType}
          onChangeValue={(value) => setVehicleType(value)}
        />
        <SelectInput
          title="QUANTO TEMPO VOCÊ VAI FICAR?"
          text={filteringTime}
          register={register}
          name="duration"
          error={errors.duration}
          disabled={!vehicleType}
          value={duration}
          onChangeValue={(value) => setDuration(value)}
        />
        <Card
          city={watch('city')}
          area={watch('area')}
          vehicle={vehicleType}
          time={Number(duration)}
          price={price}
          licencePlate={watch('licencePlate')}
        />
        <GoogleReCaptcha onVerify={recaptchaHandler} />
        <Flex gap="10px" px="5px">
          <Checkbox
            size="lg"
            borderColor="blue.600"
            colorScheme="blue"
            isChecked={isChecked}
            onChange={() => setIsChecked((prev) => !prev)}
          />
          <Text>Aceito e concordo com os</Text>
          <Text textDecoration="underline" color="#015CFF" onClick={onOpen}>
            Termos de uso
          </Text>
        </Flex>
        <MainButton
          disabled={captcha === '' || isLoading || !isChecked}
          type="submit"
          text="Gerar PIX de pagamento"
          icon={RightArrow}
          isLoading={isLoading}
        />
      </form>

      <TermosDeUsoModal isOpen={isOpen} onClose={onClose} />
    </Wrapper>
  )
}

export default WithoutQRCode
